import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { internalTargetFor } from "../lib/internalNavigation.js";

/**
 * One click handler, on the app wrapper, that turns every internal `<a href>`
 * on the site into a client-side navigation.
 *
 * ── Why a delegate and not a <Link> ───────────────────────────────────────
 * Nothing in `src/` used a router link; roughly thirty bare anchors did the
 * navigating, and two of them are not authored JSX at all (the cookie banner's
 * privacy link comes out of `<Trans>`, guide body links out of `GuideArticle`).
 * A delegate reaches all of them without touching a single piece of markup —
 * which also means the `href="/…"` assertions in the SEO tests keep passing
 * against the exact attributes that still ship — and it cannot be forgotten by
 * the next anchor someone writes.
 *
 * The cost is that a reviewer reading `<a href="/contact">` in Navbar.jsx can
 * no longer tell from the markup that it is client-side. That is what this
 * comment, and the one in `lib/internalNavigation.js`, are for.
 *
 * The decision of what to handle lives in `internalTargetFor` — a pure function
 * with its own tests. This hook is only the wiring.
 */
export default function useInternalLinkNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (event) => {
      const anchor = event.target?.closest?.("a");
      if (!anchor) return;

      const target = internalTargetFor(
        event,
        anchor,
        window.location.origin,
        location.pathname,
      );
      if (!target) return;

      event.preventDefault();
      navigate(target.pathname + target.search + target.hash);
    },
    [navigate, location.pathname],
  );
}
